import { Vector2, type Vector2Like, type Vector2Tuple } from 'three';
import { hasNumberProperties, hasAnyNumberProperties } from '@/domain/geometry/vector';

/**
 * A *Partial* set of xy values
 */
export type PartialXY = {
    readonly x?: number;
    readonly y?: number;
};

/**
 * The type of single values that can construct a Vector2
 */
export type AnyVector2 = Vector2 | Vector2Like | number[];

/**
 * Type-guard for single value that can construct a Vector2
 */
export function isAnyVector2(value: unknown): value is AnyVector2 {
    return isVector2(value) || isVector2Like(value) || isVector2Tuple(value);
}

/** Constructs a new {@link Vector2} from its argument(s)
 *
 * If a Vector2 is passed it will be cloned
 */
export function vector2(value?: AnyVector2 | number, y?: number): Vector2 {
    if (value === undefined) {
        return new Vector2();
    }
    if (typeof value === 'number') {
        if (y === undefined) {
            throw new Error(`Invalid y (${y}) parameter for creating Vector2`);
        }
        return new Vector2(value, y);
    }
    if (isVector2Tuple(value)) {
        return new Vector2(...value);
    }
    if (isVector2(value)) {
        return value.clone();
    }
    if (isPartialXY(value)) {
        return new Vector2(value.x ?? 0, value.y ?? 0);
    }
    throw new Error(`Invalid argument for creating Vector2: ${value}`);
}

/**
 * The zero Vector2
 */
export function zero2(): Vector2 {
    return new Vector2();
}

/**
 * Unit vector along +x. Horizontal reference line for angle measurements
 * (e.g., sacral slope).
 */
export function horizontalUnit2(): Vector2 {
    return new Vector2(1, 0);
}

/**
 * Unit vector along +y. Vertical reference line for angle measurements
 * (e.g., APP tilt).
 */
export function verticalUnit2(): Vector2 {
    return new Vector2(0, 1);
}

/**
 * Type-guard for a three-js {@link Vector2}
 */
export function isVector2(value: unknown): value is Vector2 {
    return value instanceof Vector2;
}

/**
 * Type-guard for three-js {@link Vector2Like}
 */
export function isVector2Like(value: unknown): value is Vector2Like {
    return hasNumberProperties(value, 'x', 'y');
}

/**
 * Type-guard for three-js {@link Vector2Tuple}
 */
export function isVector2Tuple(value: unknown): value is Vector2Tuple {
    return Array.isArray(value) && value.length === 2;
}

/**
 * Type-guard for {@link PartialXY}
 */
export function isPartialXY(value: unknown): value is PartialXY {
    return hasAnyNumberProperties(value, 'x', 'y');
}
